import { ehMagia, MAGIAS, cicloMs, recargaMs, chanceDeSucesso, iconeDaMagia, type Magia } from '../../data/magias';
import { tipoDaMagia } from '../../data/grimorio';
import { numero, t, umaCasa } from '../../i18n/idioma';
import { nomeDaMagia, nomeDoItem } from '../../i18n/nomes';
import type { BuffAtivo } from '../../world/buffs';
import { formulaLegivel } from '../../data/formulaEfeito';
import { curaDaMagia, duracaoDoEfeitoS, efeitoDe, duracaoPorMagiaS, paralisiaS, regrasEfeito, valorDoEfeito } from '../../data/combateTabela';

/*
  O conteúdo das caixas de dica (techspec-tooltips-e-efeitos). As contas são as do servidor
  (Realtime/MagiasDeEfeito.cs e CombatMath.cs), com os números do perfil publicado: GET /api/mundo/{etag}/combate
  (techspec-buff-debuff-config). Sem a tabela, valem os padrões de data/combateTabela.ts.
*/

/** "2:05", "45 s". */
export function tempoCurto(s: number): string {
  const n = Math.max(0, Math.round(s));
  if (n < 60) return t('dk.seg', { n });
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;
}

/** Minuto cheio vira "10 min"; o resto, como o tempoCurto. */
function tempoDica(s: number): string {
  const n = Math.max(0, Math.round(s));
  return n >= 60 && n % 60 === 0 ? t('dk.minutos', { n: n / 60 }) : tempoCurto(n);
}

const pct = (f: number) => Math.round(Math.max(0, f) * 100);

/**
 * Os números do texto do grimório de um buff/debuff, com o perfil publicado: {f} a fórmula do efeito,
 * {d} a duração, {pct} a fração (Reactive) e {n} o raio (Arch protection).
 */
export function argsDoEfeito(id: string): Record<string, string | number> {
  const e = efeitoDe(id);
  const r = regrasEfeito();
  const f = e?.formula ? formulaLegivel(e.formula, { MAGIA: t('grimorio.f.magia'), AVAL: t('grimorio.f.aval') }) : '';
  const d = e && e.duracaoCoef > 0
    ? t('grimorio.dur.escala', { s: numero(e.duracaoCoef), min: tempoDica(e.duracaoMin) })
    : tempoDica(e?.duracaoMin ?? 0);
  return { f, d, pct: pct(id === 'reactive-armor' ? r.reativaFracao : r.mobFator), n: r.archRaio };
}

const ATRIBUTO: Record<string, string> = {
  agility: 'dk.at.destreza', clumsy: 'dk.at.destreza',
  cunning: 'dk.at.inteligencia', feeblemind: 'dk.at.inteligencia',
  strength: 'dk.at.forca', weaken: 'dk.at.forca',
  bless: 'dk.at.tres', curse: 'dk.at.tres'
};

interface Resumo { destaque?: string; unidade?: string; linhas: string[] }

function resumoDaMagia(m: Magia, magery: number | null, evalInt: number | null): Resumo {
  const mg = magery;
  const regras = regrasEfeito();
  // Duração fixa (0 por Magia) aparece mesmo sem saber a Magia; a que escala pede a Magia ou mostra a fórmula.
  const coefDur = duracaoPorMagiaS(m.id);
  const tempo = mg == null && coefDur > 0 ? null : tempoDica(duracaoDoEfeitoS(m.id, mg ?? 0));
  const por = tempo ? t('dk.l.por', { tempo }) : t('dk.l.porFormula', { s: umaCasa(coefDur) });
  const ateGastar = () => t('dk.l.ateGastar', { tempo: tempoDica(duracaoDoEfeitoS(m.id, mg ?? 0)) });
  switch (m.id) {
    case 'magic-reflection': {
      if (mg == null) return { linhas: [t('dk.l.reflete'), ateGastar()] };
      const n = valorDoEfeito(m.id, mg, evalInt ?? 0);
      return { destaque: numero(n), unidade: t(n === 1 ? 'dk.circulo' : 'dk.circulos'),
        linhas: [t('dk.l.reflete'), t(n === 1 ? 'dk.l.refleteEx1' : 'dk.l.refleteEx', { n }), ateGastar()] };
    }
    case 'reactive-armor':
      return { destaque: mg == null ? undefined : numero(valorDoEfeito(m.id, mg, evalInt ?? 0)), unidade: t('dk.pontosDevolvidos'),
        linhas: [t('dk.l.reativa', { pct: pct(regras.reativaFracao) }), ateGastar()] };
    case 'protection':
    case 'arch-protection':
      return { destaque: mg == null ? undefined : `+${valorDoEfeito(m.id, mg, evalInt ?? 0)}`, unidade: t('dk.ar'),
        linhas: [por, m.id === 'arch-protection' ? t('dk.l.guilda', { n: regras.archRaio }) : t('dk.l.soEmVoce')] };
    case 'night-sight':
      return { destaque: tempoDica(duracaoDoEfeitoS(m.id, mg ?? 0)), unidade: t('dk.semEscuro'), linhas: [t('dk.l.emVoceOuMarcado')] };
    case 'recall':
      return { linhas: [t('dk.l.recall'), t('dk.l.recallCancela')] };
    case 'mind-blast':
      return { linhas: [t('dk.l.mente'), t('dk.l.menteAval')] };
    case 'resurrection':
      return { linhas: [t('dk.l.ress'), t('dk.l.ressCorpo'), t('dk.l.ressAceite')] };
    case 'dispel':
      return { destaque: t('dk.todos'), unidade: t('dk.buffsDoAlvo'), linhas: [t('dk.l.dispel'), t('dk.l.dispelRegra'), t('dk.l.hostil')] };
  }
  if (m.kind === 'buff' || m.kind === 'debuff') {
    const at = t(ATRIBUTO[m.id] ?? 'dk.at.tres');
    const n = mg == null ? undefined : valorDoEfeito(m.id, mg, evalInt ?? 0);
    if (m.kind === 'buff')
      return { destaque: n == null ? undefined : `+${n}`, unidade: at, linhas: [por, t('dk.l.emVoceOuMarcado'), t('dk.l.naoSomaPocao')] };
    const mob = m.id === 'feeblemind' ? t('dk.l.naoMonstro') : t(`dk.l.monstro.${m.id}`, { pct: pct(regras.mobFator) });
    return { destaque: n == null ? undefined : `−${n}`, unidade: at, linhas: [por, mob, t('dk.l.hostil')] };
  }
  switch (m.kind) {
    case 'damage': {
      const min = m.danoMin ?? 0, max = m.danoMax ?? 0;
      const f = 1 + Math.max(0, evalInt ?? 0) / 200;
      const linhas = [evalInt == null
        ? t('dk.l.baseDano', { min, max })
        : t('dk.l.baseDanoAval', { min, max, pct: Math.round(evalInt / 2) })];
      if ((m.atrasoMs ?? 0) > 0) linhas.push(t('dk.l.atraso', { s: umaCasa((m.atrasoMs ?? 0) / 1000) }));
      linhas.push(t('dk.l.interrompe'));
      return { destaque: `${Math.round(min * f)}–${Math.round(max * f)}`, unidade: t('dk.dano'), linhas };
    }
    case 'poison': {
      if (mg == null) return { linhas: [t('dk.l.venenoNiveis'), t('dk.l.venenoResist')] };
      const nivel = mg < 60 ? 'fraco' : mg < 80 ? 'medio' : 'forte';
      const dano = mg < 60 ? '1–3' : mg < 80 ? '2–5' : '4–8';
      return { destaque: t(`dk.veneno.${nivel}`), unidade: t('dk.venenoDano', { dano }), linhas: [t('dk.l.venenoNiveis'), t('dk.l.venenoResist')] };
    }
    case 'paralyze':
      return { destaque: tempoCurto(paralisiaS()), unidade: t('dk.parado'), linhas: [t('dk.l.paraSolta'), t('dk.l.paraImune')] };
    case 'cure':
      return { destaque: '100%', unidade: t('dk.antidotoFraco'), linhas: [t('dk.l.antidotoNiveis'), t('dk.l.emVoceOuMarcado')] };
    case 'heal': {
      const c = curaDaMagia(m.id);
      // A cura vai no jogador marcado, ou em você (Renato, 29/10/2026).
      if (mg == null) return { unidade: t('dk.vida'), linhas: [t('dk.l.emVoceOuMarcado')] };
      const base = Math.trunc(mg * c.coef + 1e-9);
      return { destaque: `${base + 1}–${base + c.rolagem}`, unidade: t('dk.vida'), linhas: [t('dk.l.emVoceOuMarcado')] };
    }
  }
  return { linhas: [] };
}

const TIPO_CLS: Record<string, string> = { ataque: 'dano', cura: 'cura', buff: 'buff', debuff: 'debuff', viagem: 'cura' };

/** A caixa de uma magia: no grimório, na escolha do slot e no slot da barra. */
export function DicaMagia({ id, magery, evalInt, velMagia = 0, nota }: { id: string; magery: number | null; evalInt: number | null; velMagia?: number; nota?: string }) {
  if (!ehMagia(id)) return null;
  const m = MAGIAS[id];
  const tipo = tipoDaMagia(m);
  const r = resumoDaMagia(m, magery, evalInt);
  const trancada = magery != null && magery < m.skillMin;
  const chance = magery == null ? null : chanceDeSucesso(magery, m.skillMin, m.skillFull);
  const pct = chance == null ? null : Math.round(chance * 100);
  const corChance = pct == null ? '' : pct >= 100 ? 'ok' : pct <= 0 ? 'zero' : 'meio';
  return (
    <div className="dk">
      <div className="dk__cab">
        <img src={iconeDaMagia(m.id)} alt="" draggable={false} />
        <div>
          <div className="dk__nome">{nomeDaMagia(m.id)}</div>
          <div className="dk__pal">{m.palavras}</div>
        </div>
        <span className="dk__circ">{t('dk.circuloN', { n: m.pagina })}</span>
      </div>
      {tipo && <span className={`dk__tipo dk__tipo--${TIPO_CLS[tipo]}`}>{t(`grimorio.filtro.${tipo}`)}</span>}
      {(r.destaque || r.unidade) && (
        <div className="dk__destaque">{r.destaque && <b>{r.destaque}</b>}{r.unidade && <small>{r.unidade}</small>}</div>
      )}
      {r.linhas.length > 0 && <ul className="dk__linhas">{r.linhas.slice(0, 3).map(l => <li key={l}>{l}</li>)}</ul>}
      <hr />
      <div className="dk__pe">
        <span><b>{numero(m.mana)}</b> {t('dk.mana')}</span>
        <span><b>{umaCasa(cicloMs(m.id, velMagia) / 1000)}</b> s</span>
        {recargaMs(m.id) > 0 && <span>{t('dk.recarga')} <b>{umaCasa(recargaMs(m.id) / 1000)}</b> s</span>}
        {m.alcance ? <span>{t('dk.alcance')} <b>{numero(m.alcance)}</b></span> : null}
        {!trancada && pct != null && <span className={`dk__chance--${corChance}`}>{t('dk.chance')} <b>{pct}%</b></span>}
      </div>
      {trancada
        ? <p className="dk__trava">{t('dk.trava', { min: umaCasa(m.skillMin), plena: umaCasa(m.skillFull) })}</p>
        : <div className="dk__pe dk__pe--min">{t('dk.faixa', { min: umaCasa(m.skillMin), plena: umaCasa(m.skillFull) })}</div>}
      {nota && <p className="dk__tecla">{nota}</p>}
    </div>
  );
}

/** O que um efeito ativo mostra: nome, ícone, debuff e o destaque. Bless e Curse chegam juntos num item só. */
export interface EfeitoNaBarra {
  readonly chave: string;
  readonly tipo: string;
  readonly item: string;
  readonly icone: string;
  readonly nome: string;
  readonly valor: number;
  readonly total: number;
  readonly debuff: boolean;
  readonly fim: number;
}

/** Quando o efeito apareceu pela primeira vez (para a barrinha do tempo). */
const visto = new Map<string, number>();

function fonteDoEfeito(item: string): string {
  if (!item) return '';
  return ehMagia(item) ? nomeDaMagia(item) : nomeDoItem(item);
}

/** A caixa de um efeito ativo na barra de efeitos (sua) ou na placa do alvo. */
export function DicaEfeito({ e }: { e: EfeitoNaBarra }) {
  const agora = Date.now();
  const k = `${e.chave}@${e.fim}`;
  if (!visto.has(k)) {
    visto.set(k, agora);
    if (visto.size > 64) visto.delete(visto.keys().next().value as string);
  }
  const inicio = visto.get(k) ?? agora;
  const faltaS = (e.fim - agora) / 1000;
  const frac = e.fim > inicio ? Math.max(0, Math.min(1, (e.fim - agora) / (e.fim - inicio))) : 0;
  const estoque = e.tipo === 'reflexao-magica' || e.tipo === 'armadura-reativa';
  let destaque: string | undefined;
  let unidade: string | undefined;
  const linhas: string[] = [];
  if (e.tipo === 'reflexao-magica') {
    destaque = t('dk.deTotal', { v: numero(e.valor), total: numero(Math.max(e.total, e.valor)) });
    unidade = t('dk.circulos');
    linhas.push(t(e.valor === 1 ? 'dk.l.aindaDevolve1' : 'dk.l.aindaDevolve', { n: e.valor }));
  } else if (e.tipo === 'armadura-reativa') {
    destaque = t('dk.deTotal', { v: numero(e.valor), total: numero(Math.max(e.total, e.valor)) });
    unidade = t('dk.pontos');
    linhas.push(t('dk.l.reativa', { pct: pct(regrasEfeito().reativaFracao) }));
  } else if (e.tipo === 'visao-noturna') {
    unidade = t('dk.semEscuro');
  } else if (e.valor > 0) {
    destaque = `${e.debuff ? '−' : '+'}${numero(e.valor)}`;
    unidade = e.item === 'bless' || e.item === 'curse' ? t('dk.at.tres')
      : e.tipo === 'protecao' ? t('dk.ar') : t(`buff.tipo.${e.tipo.replace(/-menos$/, '')}`);
  }
  const fonte = fonteDoEfeito(e.item);
  if (fonte) linhas.push(t('dk.l.de', { fonte }));
  return (
    <div className={`dk dk--efeito${e.debuff ? ' dk--debuff' : ''}`}>
      <div className="dk__cab dk__cab--curta">
        <img src={e.icone} alt="" draggable={false} />
        <div className="dk__nome">{e.nome}</div>
      </div>
      {(destaque || unidade) && <div className="dk__destaque">{destaque && <b>{destaque}</b>}{unidade && <small>{unidade}</small>}</div>}
      {linhas.length > 0 && <ul className="dk__linhas">{linhas.map(l => <li key={l}>{l}</li>)}</ul>}
      <hr />
      <div className="dk__pe">
        <span>{t(estoque ? 'dk.ateGastarAcaba' : 'dk.acabaEm')} <b className="dk__tempo">{tempoCurto(faltaS)}</b></span>
      </div>
      <div className="dk__barra"><i style={{ width: `${Math.round(frac * 100)}%` }} /></div>
    </div>
  );
}

/** O BuffAtivo do servidor na forma da barra (para quem monta os ícones). */
export function totalDe(b: BuffAtivo): number {
  return Math.max(b.total ?? 0, b.valor);
}
